import React, { useEffect, useState } from "react";
import UseAxiosPrivate from "../../hooks/useAxiosPrivate";
import { FEEDBACK_HISTORY_URL } from "../../utils/ApiUrls";

const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return isNaN(d.getTime()) ? value : d.toLocaleString();
};

function FeedbackHistory({ campaignCustomerId }) {
  const axiosPrivate = UseAxiosPrivate("feedback-history");
  const [history, setHistory] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | loading | loaded | error

  useEffect(() => {
    if (!campaignCustomerId) {
      setHistory([]);
      setStatus("loaded");
      return;
    }

    let cancelled = false;
    setStatus("loading");

    axiosPrivate
      .get(FEEDBACK_HISTORY_URL, { params: { campaign_customer_id: campaignCustomerId } })
      .then((res) => {
        if (cancelled) return;
        const data = res?.data;
        setHistory(Array.isArray(data) ? data : data?.results || []);
        setStatus("loaded");
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to fetch feedback history", err);
        setStatus("error");
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignCustomerId]);

  if (!campaignCustomerId) {
    return (
      <div className="py-8 text-center text-sm text-gray-400">
        No customer linked to this call yet.
      </div>
    );
  }

  if (status === "idle" || status === "loading") {
    return <div className="py-8 text-center text-sm text-gray-400">Loading history…</div>;
  }

  if (status === "error") {
    return (
      <div className="py-8 text-center text-sm text-red-500">
        Failed to load feedback history.
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-gray-400">
        No previous feedback found for this customer.
      </div>
    );
  }

  return (
    <div className="max-h-[420px] space-y-3 overflow-y-auto">
      {history.map((entry, idx) => {
        const labels = entry.feedback_labels || {};
        const questions = entry.feedback_questions || {};
        const qKeys = Object.keys(questions);

        return (
          <div key={entry.id ?? idx} className="rounded-lg border border-gray-200 p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-500">
                {formatDate(entry.changed_at)}
              </span>
              {(entry.changed_by_name || entry.agent_name) && (
                <span className="text-xs text-gray-400">
                  {entry.changed_by_name || entry.agent_name}
                </span>
              )}
            </div>

            {qKeys.length > 0 && (
              <div className="mt-2 space-y-1">
                {qKeys.map((q) => (
                  <div key={q} className="text-sm text-gray-700">
                    <span className="font-medium">{questions[q]}</span>
                    {labels[q] ? <span> — {labels[q]}</span> : null}
                  </div>
                ))}
              </div>
            )}

            {entry.comment && (
              <div className="mt-2 text-sm text-gray-600">{entry.comment}</div>
            )}

            {entry.follow_up_date && (
              <div className="mt-2 text-xs text-gray-400">
                Follow-up: {formatDate(entry.follow_up_date)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default FeedbackHistory;
